import { useState, useMemo, useEffect, useRef, useContext, useCallback, lazy, Suspense } from "react";
import type React from "react";

// Lazy-loaded timeline overlay. The timeline JS chunk is only downloaded when
// the user toggles timeline mode on for the first time, keeping the dashboard's
// initial bundle size unchanged.
const TimelineLayer = lazy(() => import("@/components/timeline/TimelineLayer"));
// Lazy-loaded sticky date/month strip that sits where the column header used
// to be in timeline mode. Loaded from its own chunk so the tree page's initial
// bundle stays unaffected when the user has timeline mode off.
const TimelineHeader = lazy(() => import("@/components/timeline/TimelineHeader"));
import { TimelineScaleProvider } from "@/components/timeline/timelineScaleContext";

const TIMELINE_MODE_STORAGE_KEY = "projectTree.isTimelineMode";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { registerViewResetOwner } from "@/lib/esc-reset-coordination";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { logger } from "@/services/debug-logger";
import { useProjectTreeData, usePulsesByProject } from "@/hooks/useProjectTreeData";
import { PulseTagRosterContext } from '@/lib/pulse-user-tags';
import { consumePinIntent } from "@/lib/quick-search/pin-intent";
import { consumePulseWorkbenchIntent } from "@/lib/pulse-workbench-intent";
import { useProjectTreeFilters } from "@/components/project-tree/hooks/useProjectTreeFilters";
import { useProjectTreeDialogs } from "@/components/project-tree/hooks/useProjectTreeDialogs";
import { useManualProjectExpansion } from "@/components/project-tree/hooks/useManualProjectExpansion";
import { useManualExpandScroll } from "@/components/project-tree/hooks/useManualExpandScroll";
import { useCanCreateInProject } from "@/hooks/useCanCreateInProject";
import { VirtualizedPulseList } from "@/components/virtualized-pulse-list";
import { VirtualizedTaskList } from "@/components/virtualized-task-list";
import { PulseTasksLoader } from "@/components/pulse-tasks-loader";
import { ProjectWithRelations, Client, User, PulseSequence, YearWithProjects, UserPreferences, Pulse } from "@shared/schema";
import { ProjectRow } from "@/components/project-row";
import { TaskRow } from "@/components/task-row";
import { PulseSettingsDialog } from "@/components/pulse-settings-dialog";
import { PulseShareDialog } from "@/components/pulse-share-dialog";
import { SequenceRow } from "@/components/sequence-row";
import { SettingsModal } from "@/components/settings-modal";
import { TeamSettingsDialog } from "@/components/team-settings-dialog";
import { AddProjectDialog } from "@/components/add-project-dialog";
import { AddYearDialog } from "@/components/add-year-dialog";
import { AddTaskDialog } from "@/components/add-task-dialog";
import { AddSequenceDialog } from "@/components/add-sequence-dialog";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { MailOutDialog } from "@/components/mail-out-dialog";
import { MilestoneDialog } from "@/components/milestone-dialog";
import { TimeEntryPopup } from "@/components/time-entry-popup";
import { UserMenu } from "@/components/user-menu";
import { NotificationBell } from "@/components/notification-bell";
import { ActivityBar } from "@/components/activity-bar";
import { EmailNotificationButton } from "@/components/email-notification-button";
import { AdminNotificationButton } from "@/components/admin-notification-button";
import { SecurityNotificationButton } from "@/components/security-notification-button";
import { SpeakerToggle } from "@/components/speaker-toggle";
import { ProjectFilterBar } from "@/components/project-filter-bar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, Info, Settings, Plus, ChevronDown, ChevronRight, Search, Check, ArrowDownAZ, MessageCircle, Send, Milestone, Clock, ChevronUp, Pin, X, Eye, Shield, Coins, Share2, CheckCircle2 } from "lucide-react";
import { AdminPulseDialog } from "@/components/admin-pulse-dialog";
import { ExpandedState } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useExpansion } from "@/contexts/expansion-context";
import { useGlobalHotkey, shouldIgnoreKeydown } from "@/hooks/use-global-hotkey";
import { registerShortcut } from "@/lib/shortcut-registry";
import { useAdminPulseVisibility } from "@/contexts/admin-pulse-visibility-context";
import { projectDisplayedPulses } from "@/lib/linked-share";
import { useAuth } from "@/contexts/auth-context";
import { DialogContext } from "@/contexts/dialog-context";
import { usePulseWorkbenchContext } from "@/contexts/pulse-workbench-context";
import { addDays, isWeekend } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useScrollPreservation } from '@/hooks/use-scroll-preservation';
import { TREE_GRID_TEMPLATE, TREE_GRID_TEMPLATE_WITH_BUDGET, getTreeColumnStyles, TREE_MIN_WIDTH, TREE_MIN_WIDTH_WITH_BUDGET, TREE_STICKY_COL_PX } from '@/lib/tree-columns';
import { compareSequenceIds } from '@/lib/sequence-utils';
import { usePulseReactionsBulk } from "@/components/pulse-reactions";
import acoloriLogo from '@assets/Acolori-Logo_1767006412108.png';
import { Component, type ErrorInfo, type ReactNode } from "react";

class AdminPulseRowGuard extends Component<
  { pulseId: number; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[AdminPulseRowGuard] Render error for pulse ${this.props.pulseId}:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="border-l-2 border-red-600/60 bg-red-950/10 px-4 py-2 text-xs text-red-400 flex items-center gap-2">
          <Shield className="h-3 w-3" />
          <span className="font-semibold">Data Error</span>
          <span className="text-red-500/60">— Admin pulse #{this.props.pulseId} contains malformed data</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// Component to fetch batch summaries and render project content
// PERFORMANCE FIX: Moved outside ProjectTree to prevent remounting on every parent re-render
const ProjectExpandedContent = ({ 
  project, 
  renderPulseTree,
  selectedSequences,
  handleSequenceToggle,
  expandedItems,
  toggleExpanded,
  renderTaskTree,
  setAddingTaskForPulse,
  addingTaskForPulse,
  actions,
  handleOpenChat,
  treeProjects,
  createEffortPulseMutation,
  handleOpenTimePopup,
  timeTrackingSettings,
  scrollTarget,
  onScrollComplete,
  manualExpandIntent,
  onManualExpandComplete,
  setPulseSettingsTarget,
  setPulseShareTarget,
  showBudgetColumn
}: { 
  project: any; 
  renderPulseTree: any;
  selectedSequences: Record<string, string[]>;
  handleSequenceToggle: (projectId: string, sequenceNumber: string) => void;
  expandedItems: any;
  toggleExpanded: (id: string) => void;
  renderTaskTree: (tasks: any[], projectId: string, depth: number) => React.ReactNode;
  setAddingTaskForPulse: (pulseId: number | null) => void;
  addingTaskForPulse: number | null;
  actions: any;
  handleOpenChat: (projectId: string, specificPulse?: any) => void;
  treeProjects: any[];
  createEffortPulseMutation: any;
  handleOpenTimePopup: (projectId: string, pulseId: number, event: React.MouseEvent) => void;
  timeTrackingSettings?: { globalEnabled: boolean; defaultProjectEnabled: boolean };
  scrollTarget?: string | null;
  onScrollComplete?: () => void;
  manualExpandIntent?: number;
  onManualExpandComplete: (projectId: string, intent: number) => void;
  setPulseSettingsTarget?: (pulse: any) => void;
  setPulseShareTarget?: (target: any) => void;
  showBudgetColumn?: boolean;
}) => {
  // Check if user has permission to create content in this project
  const { data: permissionData, isLoading } = useCanCreateInProject(project.id);
  const canCreate = permissionData?.canCreate ?? false; // Default to false while loading for security
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const { adminPulseVisibility } = useAdminPulseVisibility();
  const [adminPulseDialogOpen, setAdminPulseDialogOpen] = useState(false);
  
  // Check if project is frozen (deactivated or archived) - hide action buttons if so
  const isDeactivated = project.lifecycleState === 'deactivated';
  const isArchived = project.lifecycleState === 'archived';
  const isReadOnly = isDeactivated || isArchived;
  
  // Fetch sequences for the project
  const { data: sequences, isLoading: sequencesLoading } = useQuery({
    queryKey: ['/api/projects', project.id, 'sequences'],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/projects/${project.id}/sequences`);
      if (!response.ok) {
        throw new Error('Failed to fetch sequences');
      }
      return response.json();
    },
    enabled: !!project.id
  });

  // Get pulses from tree data instead of fetching separately
  const treeProject = useMemo(() => 
    treeProjects.find(p => p.id === project.id),
    [treeProjects, project.id]
  );
  
  const pulses = useMemo(() => treeProject?.pulses || [], [treeProject?.pulses]);
  // Task #145: shared admin-visibility filter — applied to both the virtualized
  // and non-virtualized render paths so they show the same set. Kept as a
  // separate derivation so the original `pulses` typing (and downstream
  // summaries/metadata maps that iterate the full list) are unaffected.
  const visiblePulses = useMemo(
    () => projectDisplayedPulses(pulses, isAdmin, adminPulseVisibility, selectedSequences[project.id]),
    [pulses, isAdmin, adminPulseVisibility, selectedSequences, project.id]
  );
  
  // Map tree pulse data to match existing batch data structure
  const pulseSummaries = useMemo(() => {
    const summaries: Record<number, any> = {};
    (pulses as any[]).forEach((pulse: any) => {
      summaries[pulse.id] = pulse.taskCounts; // Use taskCounts from tree
    });
    return summaries;
  }, [pulses]);
  
  const pulseMetadata = useMemo(() => {
    const metadata: Record<number, any> = {};
    pulses.forEach((pulse: any) => {
      metadata[pulse.id] = {
        sequences: pulse.sequences,
        hasTasks: pulse.hasTasks,
        timeTracking: pulse.timeTracking
      };
    });
    return metadata;
  }, [pulses]);
  
  // Extract sequences from metadata into a separate map for easier access
  const pulseSequences = useMemo(() => {
    const sequenceMap: Record<number, any[]> = {};
    pulses.forEach((pulse: any) => {
      sequenceMap[pulse.id] = pulse.sequences || [];
    });
    return sequenceMap;
  }, [pulses]);
  
  // Tree data is always loaded (no batch loading state needed)
  const isBatchLoading = false;
  const pulsesLoading = !treeProject; // Loading if tree project not found yet
  const hasExpandedPulse = visiblePulses.some((pulse: any) => expandedItems[`pulse-${pulse.id}`]);
  const useVirtualizedPulses = visiblePulses.length > 50 && !hasExpandedPulse;
  const completeManualExpansion = useCallback((intent: number) => {
    onManualExpandComplete(project.id, intent);
  }, [onManualExpandComplete, project.id]);
  const displayedPulseIds = useMemo(() => visiblePulses.map((pulse: any) => pulse.id as number), [visiblePulses]);
  useManualExpandScroll({
    // The virtualized list owns positioning and consumption when it is rendered.
    intent: useVirtualizedPulses && !scrollTarget ? undefined : manualExpandIntent,
    loading: sequencesLoading || pulsesLoading,
    explicitNavigation: !!scrollTarget,
    displayedPulseIds,
    onComplete: completeManualExpansion,
  });

  useEffect(() => {
    if (scrollTarget !== project.id) return;
    if (!pulses || pulses.length === 0) {
      const projectRow = document.getElementById(`project-row-${project.id}`);
      if (projectRow) {
        projectRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onScrollComplete?.();
      return;
    }
    const lastPulse = pulses[pulses.length - 1];
    if (!lastPulse) {
      onScrollComplete?.();
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;
    const tryScroll = () => {
      const el = document.getElementById(`pulse-row-${lastPulse.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        onScrollComplete?.();
        return;
      }
      attempts++;
      if (attempts < maxAttempts) {
        requestAnimationFrame(tryScroll);
      } else {
        const projectRow = document.getElementById(`project-row-${project.id}`);
        if (projectRow) {
          projectRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        onScrollComplete?.();
      }
    };
    requestAnimationFrame(tryScroll);
  }, [scrollTarget, project.id, pulses, onScrollComplete]);

  const pulseIds = useMemo(() => pulses.map((p: any) => p.id as number), [pulses]);
  const { data: pulseReactions } = usePulseReactionsBulk(pulseIds, project.id);

  // Show skeleton only for sequences, NOT for pulses (they're already in tree data)
  if (sequencesLoading) {
    return (
      <div className="space-y-0">
        {/* Skeleton for sequences */}
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={`seq-skeleton-${i}`}
            className="tree-item border-b border-gray-600 tree-indent-1 h-10 bg-gray-800 animate-pulse"
            style={{ minHeight: '40px' }}
          />
        ))}
      </div>
    );
  }

  const pulseElements = renderPulseTree(visiblePulses, project.id, pulseSummaries, pulseMetadata, pulseSequences, isBatchLoading, isReadOnly, pulseReactions);

  return (
    <PulseTagRosterContext.Provider value={treeProject?.tagUsers || []}>
      {/* Render sequences first - sorted: MST first, then numeric, then alpha */}
      {sequences && [...sequences]
        .sort((a: any, b: any) => compareSequenceIds(a.sequenceNumber, b.sequenceNumber))
        .map((sequence: any) => (
        <SequenceRow 
          key={sequence.id}
          sequence={sequence}
          projectId={project.id}
          projectStatus={project.projectStatus}
          isSelected={selectedSequences[project.id]?.includes(sequence.sequenceNumber)}
          onToggleSelection={(sequenceNumber) => handleSequenceToggle(project.id, sequenceNumber)}
          showBudgetColumn={showBudgetColumn}
        />
      ))}
      
      {/* Add Sequence button - only show if user has permission and project is not frozen/archived */}
      {canCreate && !isReadOnly && (
        <div className="tree-item border-b border-gray-600 tree-indent-1">
          <div className="flex px-3 items-center">
            {/* Empty expand column */}
            <div className="w-4 flex items-center justify-center flex-shrink-0 mr-1"></div>
            
            {/* Empty ID column */}
            <div className="w-24 flex items-center flex-shrink-0"></div>

            {/* Add Sequence button */}
            <div className="w-80 min-w-0 px-3 flex-shrink-0">
              <IconTooltip label="Add a new sequence">
                <button
                  onClick={() => actions.openAddSequenceDialog(project.id)}
                  className="text-sm text-gray-500 hover:text-blue-400 transition-colors flex items-center whitespace-nowrap"
                  aria-label="Add a new sequence"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Add Sequence
                </button>
              </IconTooltip>
            </div>
          </div>
        </div>
      )}
      
      {/* Then render pulses - use virtualization for large lists (unless any pulse is expanded) */}
      {(() => {
        // Use virtualization only if: >50 pulses AND no pulse is expanded
        // (virtualization doesn't support variable heights, so expanded tasks would be clipped)
        // Compute shouldShowTimeTracking for this project
        const shouldShowTimeTracking = timeTrackingSettings?.globalEnabled && (project?.timeTrackingEnabled ?? true);
        
        if (useVirtualizedPulses) {
          return (
            <VirtualizedPulseList
              pulses={visiblePulses}
              projectId={project.id}
              pulseSummaries={pulseSummaries}
              pulseSequences={pulseSequences}
              expandedItems={expandedItems}
              toggleExpanded={toggleExpanded}
              setAddingTaskForPulse={setAddingTaskForPulse}
              addingTaskForPulse={addingTaskForPulse}
              renderTaskTree={renderTaskTree}
              selectedSequences={selectedSequences[project.id]}
              pulseMetadata={pulseMetadata}
              isBatchLoading={isBatchLoading}
              onTrackTime={handleOpenTimePopup}
              shouldShowTimeTracking={shouldShowTimeTracking}
              isProjectDeactivated={isReadOnly}
              onOpenSettings={isReadOnly || !setPulseSettingsTarget ? undefined : setPulseSettingsTarget}
              onShareClick={isReadOnly || !setPulseShareTarget ? undefined : (p: any) => setPulseShareTarget({
                pulse: p,
                projectId: project.id,
                projectName: project?.projectName || project.id
              })}
              pulseReactions={pulseReactions}
              manualExpandIntent={!sequencesLoading && !pulsesLoading && !scrollTarget ? manualExpandIntent : undefined}
              onManualExpandComplete={completeManualExpansion}
            />
          );
        }
        
        // Otherwise use non-virtualized rendering
        return pulseElements;
      })()}
      {/* Add pulse row - only show if user has permission and project is not frozen/archived */}
      {canCreate && !isReadOnly && (
        <div className="tree-item border-b border-gray-600 tree-indent-1">
          <div className="flex px-3 items-center">
            {/* Empty expand column */}
            <div className="w-4 flex items-center justify-center flex-shrink-0 mr-1"></div>
            
            {/* Empty ID column */}
            <div className="w-24 flex items-center flex-shrink-0"></div>

            {/* Add Pulse, Chat, Mail Out, and Milestone buttons - spread out with more spacing */}
            <div className="flex-1 px-3 flex gap-8 items-center justify-start">
              <button
                onClick={() => {
                  actions.setSelectedProjectId(project.id);
                  actions.setShowAddTask(true);
                }}
                disabled={isLoading}
                className="text-sm text-gray-500 hover:text-blue-400 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Pulse
              </button>
              <IconTooltip label="Open project chat">
                <button
                  onClick={() => handleOpenChat(project.id)}
                  disabled={isLoading}
                  className="text-sm text-gray-500 hover:text-blue-400 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Open project chat"
                >
                  <MessageCircle className="w-3 h-3 mr-1" />
                  Chat
                </button>
              </IconTooltip>
              <IconTooltip label="Send email from the project's email-out pulse">
                <button
                  onClick={() => actions.openMailOutDialog(project.id)}
                  disabled={isLoading}
                  className="text-sm text-gray-500 hover:text-green-400 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Send email from the project's email-out pulse"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Mail Out
                </button>
              </IconTooltip>
              <IconTooltip label="Track production effort & hours">
                <button
                  onClick={() => createEffortPulseMutation.mutate(project.id)}
                  disabled={isLoading || createEffortPulseMutation.isPending}
                  className="text-sm text-gray-500 hover:text-emerald-400 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Track production effort & hours"
                >
                  <Coins className="w-3 h-3 mr-1" />
                  {createEffortPulseMutation.isPending ? 'Creating...' : 'Effort'}
                </button>
              </IconTooltip>
              <button
                onClick={() => actions.openMilestoneDialog(project.id)}
                disabled={isLoading}
                className="text-sm text-gray-500 hover:text-purple-400 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Milestone className="w-3 h-3 mr-1" />
                Milestone
              </button>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-preview-share', { detail: { projectId: project.id, shareType: 'preview' } }))}
                disabled={isLoading}
                data-testid={`button-preview-${project.id}`}
                className="text-sm text-gray-500 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: undefined }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#5f8ac9')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '')}
              >
                <Eye className="w-3 h-3 mr-1" />
                Preview
              </button>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('open-preview-share', { detail: { projectId: project.id, shareType: 'final' } }))}
                disabled={isLoading}
                data-testid={`button-final-${project.id}`}
                className="text-sm text-gray-500 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                onMouseEnter={(e) => (e.currentTarget.style.color = '#34d391')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '')}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" style={{ color: 'inherit' }} />
                Final
              </button>
              {isAdmin && (
                <IconTooltip label="Admin-only pulse (hidden from clients)">
                  <button
                    onClick={() => setAdminPulseDialogOpen(true)}
                    disabled={isLoading}
                    className="text-sm text-amber-600 hover:text-amber-400 transition-colors flex items-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Admin-only pulse (hidden from clients)"
                  >
                    <Shield className="w-3 h-3 mr-1" />
                    Admin
                  </button>
                </IconTooltip>
              )}
            </div>
          </div>
        </div>
      )}
      {isAdmin && (
        <AdminPulseDialog
          open={adminPulseDialogOpen}
          onClose={() => setAdminPulseDialogOpen(false)}
          projectId={project.id}
        />
      )}
    </PulseTagRosterContext.Provider>
  );
};

/**
 * Tracks the user's `prefers-reduced-motion: reduce` setting and updates
 * reactively when the OS-level preference changes mid-session.
 *
 * Returns `false` on the very first render (before the effect has a chance to
 * read `matchMedia`); this is fine for our use case because reduced-motion is
 * an opt-in/accommodation flag — getting one frame of "motion allowed" before
 * we honour the setting is acceptable, and we never lock the value in.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export function ProjectTree() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Team settings dialog (task #52) — open to every authenticated user
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const { openWorkbench } = usePulseWorkbenchContext();
  const { expandedItems, toggleExpanded: toggleExpandedGlobal, expandProject, expandPulse, expandMultiple, setExpanded, collapseAll, collapseAllExceptYears } = useExpansion();
  // Ready to integrate with TableRenderingManager when needed
  // const { isExpanded, toggleExpanded: toggleTableExpanded } = useTableExpansion();
  // const visibleItems = useVisibleItems(projectId);
  
  // Use dialog hook to manage all dialog states
  const { dialogs, context, actions, openTimePopup } = useProjectTreeDialogs();
  
  // Sequence filtering state - tracks selected sequences per project
  const [selectedSequences, setSelectedSequences] = useState<Record<string, string[]>>({});

  // Admin pulse visibility - volatile state only, resets on page refresh (Safe Mode).
  // Hoisted to a global context (task #145) so the project-tree and year-tree
  // share a single source of truth.
  const { adminPulseVisibility, cycleAdminPulseVisibility } = useAdminPulseVisibility();

  // Inline timeline mode toggle. Persisted to localStorage only — no server state.
  const [isTimelineMode, setIsTimelineMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(TIMELINE_MODE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleTimelineModeToggle = useCallback(() => {
    setIsTimelineMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(TIMELINE_MODE_STORAGE_KEY, next ? 'true' : 'false');
      } catch {
        /* ignore storage errors (private mode, quota, etc.) */
      }
      return next;
    });
  }, []);

  // Single-letter "t" toggles timeline mode. The hook bails on inputs,
  // contenteditable, modifiers, defaultPrevented, and open Radix overlays
  // — see useGlobalHotkey for the full safety matrix.
  useGlobalHotkey('t', handleTimelineModeToggle, {
    description: 'Zeitleiste umschalten',
    group: 'Projektbaum',
  });

  // Bumped each time the user clicks "reset timeline view"; the lazy
  // TimelineLayer watches this signal and re-applies `computeInitialScale`.
  // We also clear the persisted scale here (via a dynamic import that
  // resolves from the already-loaded timeline chunk) so a click that
  // arrives before the lazy layer has mounted still wipes localStorage —
  // the layer will then initialize from the default window on first mount.
  const [timelineResetSignal, setTimelineResetSignal] = useState(0);
  const handleTimelineResetView = useCallback(() => {
    setTimelineResetSignal((n) => n + 1);
    void import("@/components/timeline/useTimelineMetrics").then((m) =>
      m.clearPersistedScale(),
    );
  }, []);

  // Ref to the rows container, used by the lazy-loaded TimelineLayer to
  // measure each row's vertical position so bars can be aligned per-row
  // without modifying any of the row components.
  const rowsContainerRef = useRef<HTMLDivElement>(null);

  // Ref to the horizontally-scrolling `.sticky-table-container`. Passed to
  // `TimelineHeader` so it can mirror the container's `scrollLeft` via a
  // CSS transform on its segments — keeping the date strip aligned with
  // the bars below at viewport widths < `activeMinWidth` (~1250px), where
  // the rows scroll horizontally but the header (which lives outside the
  // scroll container so its `position: sticky` can anchor to the viewport)
  // would otherwise stay put.
  //
  // We hold the DOM node in *state* (not in a `useRef`) so that React's
  // ref-callback pattern re-runs subscribers whenever the node identity
  // changes. A plain `useRef` is a stable object whose `.current` mutates
  // silently — any consumer that captured the ref in a `useEffect` deps
  // array would never re-attach its scroll listener if the container
  // remounted (e.g. on a route change or conditional re-render). Backing
  // the ref with state forces a re-render and effect re-run on every node
  // identity change, which is what we want.
  const [tableScrollContainer, setTableScrollContainer] =
    useState<HTMLDivElement | null>(null);

  // ── Column-header fade + collapse for timeline mode (Task #115) ─────────
  // The dark sticky column-header bar (Lead / Artist / Project Status / etc.)
  // is meaningless in timeline mode and visually overlaps the timeline
  // overlay's top-right controls (Today jump, direction chevrons, reset
  // view). We hide it entirely when timeline mode is on and collapse the
  // space it used so the timeline rows + overlay sit directly under the
  // top filter bar.
  //
  // Animation is fade-only (~150ms) to match the existing column-slide
  // treatment, and respects `prefers-reduced-motion: reduce` with an
  // instant swap. We never interpolate height or grid-template-rows: the
  // header is removed from layout *after* the opacity transition completes
  // (or synchronously when reduced motion is set), which avoids the visible
  // jitter that comes from desyncing layout properties with compositor-only
  // opacity transitions.
  const prefersReducedMotion = usePrefersReducedMotion();
  // `headerInLayout` controls whether the header element is present in the
  // DOM at all (i.e. whether it occupies layout space). `headerVisible`
  // controls its opacity. The two together implement: fade-out → unmount,
  // and remount → fade-in.
  const [headerInLayout, setHeaderInLayout] = useState<boolean>(() => !isTimelineMode);
  const [headerVisible, setHeaderVisible] = useState<boolean>(() => !isTimelineMode);
  useEffect(() => {
    if (isTimelineMode) {
      if (prefersReducedMotion) {
        // Instant swap: drop opacity and unmount in the same tick so no
        // transition is observed.
        setHeaderVisible(false);
        setHeaderInLayout(false);
        return;
      }
      // Start the fade-out. The element stays mounted (occupying layout)
      // until the opacity transition completes, at which point
      // `handleHeaderTransitionEnd` removes it from layout.
      setHeaderVisible(false);
      return;
    }
    // Timeline mode turning OFF.
    if (prefersReducedMotion) {
      setHeaderInLayout(true);
      setHeaderVisible(true);
      return;
    }
    // Re-mount the header in its faded state first so the browser commits
    // a frame at opacity 0, then flip to opacity 1 on the next frame to
    // produce a visible fade-in transition. A single rAF is not enough on
    // some browsers (the style change can coalesce with the mount commit
    // and skip the transition); double rAF guarantees a paint between the
    // two opacity values.
    setHeaderInLayout(true);
    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        setHeaderVisible(true);
      });
    });
    return () => {
      cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
    };
  }, [isTimelineMode, prefersReducedMotion]);

  const handleHeaderTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      // Only react to the wrapper's own opacity transition — child elements
      // (e.g. sort buttons that have their own hover transitions) bubble
      // transitionend up to us and would otherwise spuriously trigger
      // unmount mid-fade.
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      // Only the fade-OUT case removes the element from layout. The fade-IN
      // case ends with the element already mounted and visible, so there is
      // nothing further to do.
      if (isTimelineMode && !headerVisible) {
        setHeaderInLayout(false);
      }
    },
    [isTimelineMode, headerVisible],
  );

  // Effort pulse creation mutation
  const createEffortPulseMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const response = await apiRequest('POST', '/api/pulses', {
        projectId,
        pulseDescription: 'Effort Estimation',
        category: 'effort',
        status: 'working_on_it',
        attachments: [],
        htmlContent: null
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create effort pulse');
      }
      
      return response.json();
    },
    onSuccess: (newPulse: { id?: number }) => {
      // Open the effort details immediately so the user doesn't wait for the
      // full tree snapshot to refetch (task #293). The workbench fetches the
      // pulse on its own; the row still appears via the tree invalidation +
      // realtime insert below.
      if (newPulse?.id) {
        openWorkbench(newPulse.id);
      }
      queryClient.invalidateQueries({ queryKey: ['/api/tree/snapshot'] });
      toast({
        title: "Effort pulse created",
        description: "Effort estimation pulse has been added successfully.",
      });
    },
    onError: (error: any) => {
      console.error('Failed to create effort pulse:', error);
      toast({
        title: "Error",
        description: error?.message || "Failed to create effort pulse. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Get aggregated tree data with pulses
  // IMPORTANT: Use stable options object to prevent cache misses on re-renders
  const treeDataOptions = useMemo(() => ({ 
    includePulses: true  // Include pulse data with metadata
  }), []);
  const { years: treeYears, projects: treeProjects, isLoading: treeLoading } = useProjectTreeData(treeDataOptions);
  const displayedPulseCounts = useMemo(() => Object.fromEntries(treeProjects.map(project => [
    project.id,
    projectDisplayedPulses(project.pulses, isAdmin, adminPulseVisibility, selectedSequences[project.id]).length,
  ])), [treeProjects, isAdmin, adminPulseVisibility, selectedSequences]);

  // Fetch clients and artists separately
  const { data: clients, isLoading: clientsLoading } = useQuery<Client[]>({
    queryKey: ['/api/clients'],
  });
  
  const { data: artists, isLoading: artistsLoading } = useQuery<User[]>({
    queryKey: ['/api/artists'],
  });
  
  const { data: userPreferences } = useQuery<UserPreferences>({
    queryKey: ['/api/user/preferences'],
  });
  
  // Fetch time tracking settings once at the top level (prevents N+1 queries in TaskRow)
  const { data: timeTrackingSettings } = useQuery<{ globalEnabled: boolean; defaultProjectEnabled: boolean }>({
    queryKey: ['/api/time-tracking/settings'],
  });
  
  const showBudgetColumn = isAdmin && adminPulseVisibility !== 'hidden';
  
  const budgetProjectIds = useMemo(() => {
    if (!treeYears || !Array.isArray(treeYears)) return '';
    const ids: string[] = [];
    for (const year of treeYears as any[]) {
      if (!expandedItems[`year-${year.id}`]) continue;
      for (const project of year.projects || []) {
        ids.push(project.id);
      }
    }
    return ids.join(',');
  }, [treeYears, expandedItems]);

  const { data: bulkBudgetData } = useQuery<Record<string, any[]>>({
    queryKey: ['/api/budget-items-bulk', budgetProjectIds],
    queryFn: async () => {
      if (!budgetProjectIds) return {};
      const res = await fetch(`/api/budget-items-bulk?projectIds=${budgetProjectIds}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch budget data');
      return res.json();
    },
    enabled: showBudgetColumn && !!budgetProjectIds,
    staleTime: 30_000,
  });

  const activeGridTemplate = showBudgetColumn ? TREE_GRID_TEMPLATE_WITH_BUDGET : TREE_GRID_TEMPLATE;
  const activeColumnStyles = getTreeColumnStyles(showBudgetColumn);
  const activeMinWidth = showBudgetColumn ? TREE_MIN_WIDTH_WITH_BUDGET : TREE_MIN_WIDTH;

  // Stable handler for opening time tracking popup (state lifted from TaskRow)
  // Uses directly exported openTimePopup for optimal callback stability (no actions dependency)
  const handleOpenTimePopup = useCallback((projectId: string, pulseId: number, event: React.MouseEvent) => {
    openTimePopup(projectId, pulseId, { x: event.clientX, y: event.clientY });
  }, [openTimePopup]);
  
  // Combine all data
  const data = {
    years: treeYears as any,
    clients: clients || [],
    artists: artists || [],
    userPreferences,
    isLoading: treeLoading || clientsLoading || artistsLoading,
    yearsLoading: treeLoading,
    clientsLoading,
    artistsLoading
  };

  // Use the filters hook - manages all filter/sort state and auto-saves preferences
  const filters = useProjectTreeFilters({ userPreferences: data.userPreferences, years: data.years });

  // Filter-shortcut handlers (task #230). `p` toggles "Show only pinned",
  // `f` toggles "Hide Finals". The handlers read from a ref so the
  // `useGlobalHotkey` registration stays stable while still toggling the
  // latest value on each keypress.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const handlePinnedShortcut = useCallback(() => {
    filtersRef.current.actions.setPinnedProjectsEnabled(
      !filtersRef.current.filters.pinnedEnabled,
    );
  }, []);
  const handleHideFinalsShortcut = useCallback(() => {
    filtersRef.current.actions.setHideFinals(
      !filtersRef.current.filters.hideFinals,
    );
  }, []);
  const handleHideWithEffortsShortcut = useCallback(() => {
    filtersRef.current.actions.setHideWithEfforts(
      !filtersRef.current.filters.hideWithEfforts,
    );
  }, []);
  useGlobalHotkey('p', handlePinnedShortcut, {
    description: 'Filter: Nur Angeheftete',
    group: 'Projektbaum',
  });
  useGlobalHotkey('f', handleHideFinalsShortcut, {
    description: 'Filter: Hide Finals',
    group: 'Projektbaum',
  });
  useGlobalHotkey('e', handleHideWithEffortsShortcut, {
    description: 'Filter: Hide with Efforts',
    group: 'Projektbaum',
  });

  // Two-step ESC resets the tree to its standard view. First press shows a
  // confirmation toast and "arms" the reset for 2s; a second ESC within that
  // window clears filters (search, client, artist, pinned-only, hide-finals),
  // clears column sorting, turns timeline mode off, and collapses projects/pulses/tasks. Year-level
  // expansion is KEPT (per user preference) so the current year stays open
  // after a reset. Scroll position, the currently-open task, and the user's
  // pins themselves are also preserved — only the "show only pinned" filter
  // is turned off, not the pinning. `useGlobalHotkey` already bails on
  // inputs, contenteditable, modifiers, and open Radix overlays, so ESC
  // keeps its native close-the-current-dialog behaviour everywhere else.
  const escResetArmedAtRef = useRef<number | null>(null);
  const { data: availabilityStatuses } = useQuery<Array<{ id: number; availabilityStatus?: string }>>({
    queryKey: ['/api/users/availability-statuses'],
    enabled: !!user?.id,
  });
  const isAway = Boolean(
    user?.id && (availabilityStatuses?.find((s) => s.id === user.id)?.availabilityStatus || 'working') !== 'working',
  );
  const isAwayRef = useRef(isAway);
  isAwayRef.current = isAway;
  const handleGlobalReset = useCallback((event: KeyboardEvent) => {
    // Ignore auto-repeat when the key is held down — otherwise a held ESC
    // arms and immediately fires the reset within milliseconds, defeating
    // the entire point of the two-step confirmation.
    if (event.repeat) return;
    const now = Date.now();
    const armedAt = escResetArmedAtRef.current;
    if (armedAt !== null && now - armedAt <= 2000) {
      escResetArmedAtRef.current = null;
      filtersRef.current.actions.clearFilters();
      filtersRef.current.actions.resetSorting();
      setIsTimelineMode(false);
      try {
        window.localStorage.setItem(TIMELINE_MODE_STORAGE_KEY, 'false');
      } catch {
        /* ignore storage errors (private mode, quota, etc.) */
      }
      collapseAllExceptYears();
      // The availability status PATCH lives in useEscStatusReset (app-wide),
      // which fires on this same double-ESC; we own the (single) toast here
      // and word it to cover both — see esc-reset-coordination.ts.
      toast({
        description: isAwayRef.current
          ? 'View and status reset to default.'
          : 'View reset to default.',
      });
      return;
    }
    escResetArmedAtRef.current = now;
    toast({
      description: isAwayRef.current
        ? 'Press ESC again to reset view and switch status back to Working.'
        : 'Press ESC again to reset view.',
      duration: 2000,
    });
  }, [collapseAllExceptYears, toast]);
  useGlobalHotkey('Escape', handleGlobalReset, {
    description: 'Reset view (press twice)',
    group: 'Projektbaum',
  });
  useEffect(() => registerViewResetOwner(), []);

  // Dynamic Shift+[letter] member shortcuts (task #230). One single keydown
  // listener walks the latest `artists` list on every press — the listener
  // itself never re-binds (it reads via `artistsRef`), so a roster change
  // doesn't churn the global listener; only the shortcut-registry sync
  // effect below re-runs to keep the `?` help overlay accurate.
  const artistsRef = useRef(data.artists);
  artistsRef.current = data.artists;
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!event.shiftKey) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (shouldIgnoreKeydown(event)) return;
      const pressed = event.key?.toLowerCase();
      if (!pressed || pressed.length !== 1) return;
      const list = artistsRef.current || [];
      // First-registered wins on duplicate first letters (documented).
      const match = list.find(
        (a) => a.shortCode && a.shortCode[0]?.toLowerCase() === pressed,
      );
      if (!match || !match.shortCode) return;
      const current = filtersRef.current.filters.leadArtist;
      filtersRef.current.actions.setLeadArtistFilter(
        current === match.shortCode ? '' : match.shortCode,
      );
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Keep the shortcut help registry in sync with the artists roster. One
  // `registerShortcut` call per artist, keyed `Shift+[Letter]`. First-letter
  // collisions are resolved by keeping the FIRST artist (matches the
  // listener's `find` semantics above).
  useEffect(() => {
    const list = data.artists || [];
    const cleanups: Array<() => void> = [];
    const seen = new Set<string>();
    for (const artist of list) {
      const letter = artist.shortCode?.[0]?.toLowerCase();
      if (!letter) continue;
      if (seen.has(letter)) continue;
      seen.add(letter);
      const upper = letter.toUpperCase();
      const name = [artist.firstName, artist.lastName].filter(Boolean).join(' ') || artist.shortCode || upper;
      cleanups.push(
        registerShortcut({
          id: `Projektbaum::Shift+${upper}::artist-${artist.shortCode}`,
          key: `Shift+${upper}`,
          description: `Filter: ${name}`,
          group: 'Projektbaum',
        }),
      );
    }
    return () => {
      for (const fn of cleanups) fn();
    };
  }, [data.artists]);

  // Simplified toggle - no prefetching needed, tree data already has everything
  const toggleExpanded = (id: string) => {
    toggleExpandedGlobal(id);
  };
  const { manualExpandIntents, toggleProjectManually, completeManualExpansion } =
    useManualProjectExpansion(expandedItems, toggleExpanded);

  // Handle sequence selection for filtering
  const handleSequenceToggle = (projectId: string, sequenceNumber: string) => {
    setSelectedSequences(prev => {
      const projectSequences = prev[projectId] || [];
      const isCurrentlySelected = projectSequences.includes(sequenceNumber);
      
      if (isCurrentlySelected) {
        // Remove sequence from selection
        const newSequences = projectSequences.filter(seq => seq !== sequenceNumber);
        return {
          ...prev,
          [projectId]: newSequences
        };
      } else {
        // Add sequence to selection
        return {
          ...prev,
          [projectId]: [...projectSequences, sequenceNumber]
        };
      }
    });
  };

  const renderTaskTree = (tasks: any[], projectId: string, depth = 0) => {
    return tasks?.map((task) => (
      <div key={task.id}>
        <TaskRow
          task={task}
          projectId={projectId}
          depth={depth}
          isExpanded={expandedItems[`task-${task.id}`] || false}
          onToggleExpanded={() => toggleExpanded(`task-${task.id}`)}
          showBudgetColumn={showBudgetColumn}
        />
        {/* Subtasks removed - tasks no longer have children */}
      </div>
    )) || [];
  };

  // State to track which pulse is adding a task inline
  const [addingTaskForPulse, setAddingTaskForPulse] = useState<number | null>(null);
  const [pulseSettingsTarget, setPulseSettingsTarget] = useState<Pulse | null>(null);
  const [pulseShareTarget, setPulseShareTarget] = useState<{ pulse: Pulse; projectId: string; projectName: string } | null>(null);

  // Function to handle opening chat for a project
  // Emits open-project-channel event to open project's channel in ChatDock UI
  const handleOpenChat = useCallback((projectId: string, specificPulse?: any, targetMessageId?: number | null, channelId?: string, channelType?: string) => {
    console.log('[ProjectTree] Opening chat for project via ChatDock:', projectId, targetMessageId ? `jump to msg ${targetMessageId}` : '', channelId ? `channelId: ${channelId}` : '', channelType ? `type: ${channelType}` : '');
    
    const event = new CustomEvent('open-project-channel', {
      detail: { projectId, targetMessageId, channelId, channelType }
    });
    window.dispatchEvent(event);
  }, []);

  // Listen for chat opening requests from other components (e.g., notification bell)
  // Routes to the new ChatDock UI via open-project-channel event
  useEffect(() => {
    const handleOpenChatRequest = (event: Event) => {
      const customEvent = event as CustomEvent<{ projectId: string; pulseId: number | null; pulseDateAdded?: string; targetMessageId?: number | null; channelId?: string; channelType?: string }>;
      const { projectId, targetMessageId, channelId, channelType } = customEvent.detail;
      
      console.log('[Chat Event] Received open-chat-pulse event, routing to ChatDock:', { projectId, targetMessageId, channelId, channelType });
      
      handleOpenChat(projectId, undefined, targetMessageId, channelId, channelType);
    };
    
    window.addEventListener('open-chat-pulse', handleOpenChatRequest);
    console.log('[Chat Event] Event listener registered for open-chat-pulse');
    return () => {
      window.removeEventListener('open-chat-pulse', handleOpenChatRequest);
      console.log('[Chat Event] Event listener unregistered');
    };
  }, [handleOpenChat]);

  useEffect(() => {
    const handleOpenPulseWorkbench = (event: Event) => {
      const customEvent = event as CustomEvent<{ pulseId: number }>;
      const { pulseId } = customEvent.detail;
      if (pulseId) {
        // Fast path — the tree was mounted when the event fired. Clear any
        // matching durable intent so it can't re-open on a later remount.
        consumePulseWorkbenchIntent();
        openWorkbench(pulseId);
      }
    };

    window.addEventListener('open-pulse-workbench', handleOpenPulseWorkbench);

    // Task #50 — durable intent consumer: when the workbench-open request
    // was made from a route where this tree wasn't mounted yet (or on a
    // cold start slower than the dispatcher's one-frame deferral), the
    // event above was lost. The intent module survives the transition;
    // consume it once on mount so the click never silently does nothing.
    const intent = consumePulseWorkbenchIntent();
    if (intent) {
      openWorkbench(intent.pulseId);
    }

    return () => {
      window.removeEventListener('open-pulse-workbench', handleOpenPulseWorkbench);
    };
  }, [openWorkbench]);

  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  useEffect(() => {
    const handleNav = (e: Event) => {
      const customEvent = e as CustomEvent<{ projectId: string }>;
      const targetId = customEvent.detail?.projectId;
      if (!targetId) return;

      const yearForProject = (filters.filteredYears || []).find((year: any) =>
        year.projects?.some((p: any) => p.id === targetId)
      );
      const idsToExpand: string[] = [];
      if (yearForProject) {
        idsToExpand.push(`year-${yearForProject.id}`);
      }
      idsToExpand.push(`project-${targetId}`);
      expandMultiple(idsToExpand);

      setScrollTarget(targetId);
    };

    window.addEventListener('navigate-to-project', handleNav);
    return () => window.removeEventListener('navigate-to-project', handleNav);
  }, [expandMultiple, filters.filteredYears]);

  // Pin-and-focus from quick search: replace pinned list with the
  // single chosen project, enable "show only pinned", and expand
  // that project's row. We look up the year using the unfiltered
  // `data.years` (not filteredYears) because the filter may not yet
  // include the target project on first render.
  const applyPinAndFocus = useCallback((targetId: string, title: string) => {
    filters.actions.setPinnedProjects([{ projectId: targetId, title }]);
    filters.actions.setPinnedProjectsEnabled(true);

    const yearForProject = (data?.years || []).find((year: any) =>
      year.projects?.some((p: any) => p.id === targetId),
    );
    const idsToExpand: string[] = [];
    if (yearForProject) idsToExpand.push(`year-${yearForProject.id}`);
    idsToExpand.push(`project-${targetId}`);
    expandMultiple(idsToExpand);

    setScrollTarget(targetId);
  }, [expandMultiple, data?.years, filters.actions]);

  useEffect(() => {
    const handlePinAndFocus = (e: Event) => {
      const ce = e as CustomEvent<{ projectId: string; title: string }>;
      const targetId = ce.detail?.projectId;
      const title = ce.detail?.title;
      if (!targetId || !title) return;
      applyPinAndFocus(targetId, title);
    };

    window.addEventListener('pin-and-focus-project', handlePinAndFocus);
    return () => window.removeEventListener('pin-and-focus-project', handlePinAndFocus);
  }, [applyPinAndFocus]);

  // Durable pin-intent consumer: when the user pinned from a route
  // where this tree wasn't mounted yet, the fast-path event was lost.
  // The intent module survives the route transition; consume it once
  // `data.years` is loaded (so the year lookup succeeds).
  useEffect(() => {
    if (!data?.years) return;
    const intent = consumePinIntent();
    if (!intent) return;
    applyPinAndFocus(intent.projectId, intent.title);
  }, [data?.years, applyPinAndFocus]);

  const handleScrollComplete = useCallback(() => {
    setScrollTarget(null);
  }, []);

  const renderYearHeader = (year: YearWithProjects) => {
    const isYearExpanded = expandedItems[`year-${year.id}`] || false;
    
    return (
      <div className="tree-item border-b border-gray-600" style={{ backgroundColor: '#364561' }}>
        <div className="flex px-3 items-center py-3 ml-[-52px]">
          {/* Empty space for sorting icon alignment */}
          <div className="w-12 flex items-center justify-center flex-shrink-0 mr-1">
            {/* Placeholder for sort icon alignment */}
          </div>
          
          {/* Expand Column - Matches project row */}
          <div className="w-4 flex items-center justify-center flex-shrink-0 border-r border-gray-600 mr-1">
            <Button
              variant="ghost"
              size="sm"
              className="expandable-row p-0 h-auto"
              onClick={() => toggleExpanded(`year-${year.id}`)}
            >
              <ChevronDown
                className={cn(
                  "w-4 h-4 text-gray-400 transition-transform expand-icon",
                  isYearExpanded ? "expanded" : "collapsed"
                )}
              />
            </Button>
          </div>
          
          {/* Year ID Column */}
          <div className="w-24 flex items-center flex-shrink-0 border-r border-gray-600 px-2">
            <span className="text-lg font-bold text-white">{year.fullYear}</span>
          </div>
          
          {/* Year Description Column - spans multiple columns */}
          <div className="flex-1 px-3 flex items-center">
            <span className="text-white">{year.description || `Year ${year.fullYear}`}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderPulseTree = (pulses: any[], projectId: string, pulseSummaries?: Record<number, any>, pulseMetadata?: Record<number, any>, pulseSequences?: Record<number, any[]>, isBatchLoading?: boolean, isProjectDeactivated?: boolean, pulseReactions?: Record<string, any[]>) => {
    // Compute shouldShowTimeTracking ONCE per project (prevents N+1 queries)
    const project = treeProjects.find((p: any) => p.id === projectId);
    const shouldShowTimeTracking = timeTrackingSettings?.globalEnabled && (project?.timeTrackingEnabled ?? true);
    
    // Already projected by ProjectExpandedContent BEFORE either render path.
    const filteredPulses = pulses;

    const result = filteredPulses?.map((pulse) => {
      const isAdminPulse = pulse.category === 'admin';
      const pulseContent = (
        <div id={`pulse-row-${pulse.id}`} className={isAdminPulse ? 'border-l-2 border-amber-600/60 bg-amber-950/10' : ''}>
          <TaskRow
            task={pulse}
            projectId={projectId}
            depth={0}
            isExpanded={expandedItems[`pulse-${pulse.id}`] || false}
            onToggleExpanded={() => toggleExpanded(`pulse-${pulse.id}`)}
            isPulse={true}
            pulseSummary={pulseSummaries?.[pulse.id]}
            pulseMetadata={pulseMetadata?.[pulse.id]}
            isBatchContext={isBatchLoading}
            onAddTaskClick={isProjectDeactivated ? undefined : () => {
              if (!expandedItems[`pulse-${pulse.id}`]) {
                toggleExpanded(`pulse-${pulse.id}`);
              }
              setAddingTaskForPulse(pulse.id);
            }}
            onTrackTime={(e) => handleOpenTimePopup(projectId, pulse.id, e)}
            shouldShowTimeTracking={shouldShowTimeTracking}
            onOpenSettings={isProjectDeactivated ? undefined : setPulseSettingsTarget}
            onShareClick={isProjectDeactivated ? undefined : (p) => setPulseShareTarget({ 
              pulse: p, 
              projectId, 
              projectName: project?.projectName || projectId 
            })}
            showBudgetColumn={showBudgetColumn}
            pulseReactions={pulseReactions?.[String(pulse.id)]}
          />
          {expandedItems[`pulse-${pulse.id}`] && (
            <PulseTasksLoader 
              pulseId={pulse.id} 
              projectId={projectId}
              toggleExpanded={toggleExpanded} 
              renderTaskTree={renderTaskTree}
              shouldStartAddingTask={addingTaskForPulse === pulse.id}
              onAddingTaskComplete={() => setAddingTaskForPulse(null)}
              isProjectDeactivated={isProjectDeactivated}
              showBudgetColumn={showBudgetColumn}
            />
          )}
        </div>
      );
      return (
        <div key={`pulse-${pulse.id}`}>
          {isAdminPulse ? (
            <AdminPulseRowGuard pulseId={pulse.id}>
              {pulseContent}
            </AdminPulseRowGuard>
          ) : pulseContent}
        </div>
      );
    }) || [];
    
    return result;
  };

  if (data.yearsLoading) {
    return (
      <div className="min-h-screen navy-bg flex items-center justify-center">
        <div className="text-gray-400">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen navy-bg pb-[var(--chat-dock-height,100px)] transition-[padding-bottom] duration-300 ease-in-out">
      {/* Header */}
      <div className="border-b border-gray-600 navy-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src={acoloriLogo} alt="Acolori" className="h-6" />
            <h1 className="text-xl font-semibold text-white">Project Manager</h1>
            <span className="text-sm text-gray-400">team.update</span>
          </div>
          <div className="flex items-center space-x-3">
            {/* Main settings — strictly admin-only (backup section included) */}
            {user?.role === 'admin' && (
              <Button 
                variant="outline" 
                className="navy-card border-gray-600 text-gray-300 hover:bg-gray-700"
                onClick={() => actions.setShowSettings(true)}
              >
                <Settings className="w-4 h-4" />
              </Button>
            )}
            {/* Team Share (task #52) — internal roles only (admin/team); hosts shared links + keychain */}
            {(user?.role === 'admin' || user?.role === 'team') && (
              <IconTooltip label="Team Share">
                <Button
                  variant="outline"
                  className="navy-card border-gray-600 text-gray-300 hover:bg-gray-700"
                  onClick={() => setShowTeamSettings(true)}
                  aria-label="Team Share"
                  data-testid="button-team-settings"
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </IconTooltip>
            )}
            <UserMenu />
          </div>
        </div>
      </div>
      {/* Compact Filtering Row */}
      <div className="sticky top-0 z-50 bg-gray-700 px-6 py-1 border-b border-gray-600">
        <div className="flex items-center justify-between transition-[min-width] duration-200" style={{ minWidth: activeMinWidth }}>
          {/* Left side - Notification buttons */}
          <div className="flex items-center space-x-3">
            {/* Notification Bell - Always visible in sticky filter bar */}
            <NotificationBell />
            
            {/* Email Notification Button - Email matches with purple badge */}
            <EmailNotificationButton />
            
            {/* Admin and Security Notification Buttons - Admin only */}
            {user?.role === 'admin' && (
              <>
                <AdminNotificationButton />
                <SecurityNotificationButton />
              </>
            )}
            
            {/* Global Speaker Toggle - Mute/unmute all notification sounds */}
            <SpeakerToggle />
          </div>
          
          {/* Right side - Filters and Pinned */}
          <div className="flex items-center space-x-3">
            {isAdmin && (
              <button
                onClick={cycleAdminPulseVisibility}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors",
                  adminPulseVisibility === 'hidden' 
                    ? "border-gray-600 text-gray-500 hover:text-amber-400 hover:border-amber-600"
                    : adminPulseVisibility === 'level_1'
                    ? "border-amber-600 text-amber-400 bg-amber-950/30"
                    : "border-red-600 text-red-400 bg-red-950/30"
                )}
                title={`Admin Pulses: ${adminPulseVisibility === 'hidden' ? 'Hidden' : adminPulseVisibility === 'level_1' ? 'Level 1 Only' : 'All Levels'}`}
              >
                <Shield className="w-3 h-3" />
                {adminPulseVisibility === 'hidden' ? 'Off' : adminPulseVisibility === 'level_1' ? 'L1' : 'All'}
              </button>
            )}

            <ProjectFilterBar
              searchFilter={filters.filters.search}
              onSearchChange={filters.actions.setSearchFilter}
              isTimelineMode={isTimelineMode}
              onTimelineModeToggle={handleTimelineModeToggle}
              onTimelineResetView={handleTimelineResetView}
              pinnedEnabled={filters.filters.pinnedEnabled}
              onPinnedToggle={filters.actions.setPinnedProjectsEnabled}
              hideFinals={filters.filters.hideFinals}
              onHideFinalsToggle={filters.actions.setHideFinals}
              hideCanceled={filters.filters.hideCanceled}
              onHideCanceledToggle={filters.actions.setHideCanceled}
              hideWithEfforts={filters.filters.hideWithEfforts}
              onHideWithEffortsToggle={filters.actions.setHideWithEfforts}
              clientFilter={filters.filters.client}
              leadArtistFilter={filters.filters.leadArtist}
              onClientFilterChange={filters.actions.setClientFilter}
              onLeadArtistFilterChange={filters.actions.setLeadArtistFilter}
              clients={data.clients || []}
              artists={data.artists || []}
              pinnedProjects={filters.filters.pinnedProjects}
              onPinnedProjectsChange={filters.actions.setPinnedProjects}
              pinnedOpen={filters.comboBoxes.pinnedOpen}
              onPinnedOpenChange={filters.comboBoxes.setPinnedOpen}
              showAddToPinned={filters.comboBoxes.showAddToPinned}
              onShowAddToPinnedChange={filters.comboBoxes.setShowAddToPinned}
              filteredYears={filters.filteredYears}
              hasActiveFilters={filters.hasActiveFilters}
              onClearFilters={filters.actions.clearFilters}
            />

          </div>
        </div>
      </div>
      {/* Wrap the timeline date strip + the scrollable table + the timeline
          overlay in a single TimelineScaleProvider so the sticky
          `TimelineHeader` (rendered as a sibling ABOVE the scroll container)
          and the `TimelineLayer` overlay (inside the scroll container) share
          the same pan/zoom state. The provider is intentionally always
          mounted so toggling timeline mode on/off doesn't destroy/recreate
          the state.

          Critically, the date strip must NOT live inside
          `.sticky-table-container`: that element has `overflow-x: auto`,
          which (per CSS spec) implicitly forces `overflow-y` to `auto` even
          though it's set to `visible`. That makes the container a vertical
          scroll container, which "captures" any `position: sticky`
          descendant — they stick relative to the container instead of the
          viewport, and since the container scrolls with the page they
          silently scroll away. Hoisting the header out of that container
          puts it back in the page-level scroll context where the
          `sticky top-12` actually pins to the viewport under the filter
          bar. Alignment with the timeline bars below is preserved because
          both the header and the overlay position their content via
          `dateToX(scale, ms)` (i.e. via `scale.panX` from the shared
          context), not via container scroll position. */}
      <TimelineScaleProvider>
        {/* Shared tall parent: gives the sticky `TimelineHeader` a containing
            box that extends through the entire scrollable rows region. Without
            this wrapper, the header's parent would be just the small
            `<div className="px-6">` immediately around it (~41px tall), and
            `position: sticky` would un-stick the moment that parent scrolled
            past the viewport — making the header track the page scroll
            instead of pinning. Plain `<div>` (no `position`, no `overflow`,
            no z-index) so it doesn't reintroduce the overflow-trap that
            hoisting the header out of `.sticky-table-container` was meant to
            solve, and so it doesn't disturb any existing positioned
            descendants. */}
        <div>
          {/* Sticky date / month strip that replaces the (hidden) column
              header in timeline mode. Lazy-loaded so the timeline JS chunk
              stays off the initial bundle.

              CRITICAL: `<TimelineHeader>` (and its `<Suspense>` boundary,
              which is not a DOM node) must be a DIRECT child of the tall
              shared wrapper above. `position: sticky` is bounded by its
              element's CONTAINING BLOCK — i.e., its direct parent's box —
              not by a grandparent. Wrapping the header in a small extra
              `<div>` (e.g. for padding) would re-introduce the previous
              regression where the header un-sticks the moment that small
              parent scrolls past the viewport top, even though the
              grandparent is tall enough. The header's own outer `<div>`
              already paints edge-to-edge by design (the dark date strip is
              full-bleed), so no horizontal-padding wrapper is needed here. */}
          {isTimelineMode && (
            <Suspense fallback={null}>
              <TimelineHeader
                leftOffsetPx={TREE_STICKY_COL_PX}
                scrollContainer={tableScrollContainer}
              />
            </Suspense>
          )}
          {/* Activity bar — active calls to join, scheduled calls, latest
              previews. Sits BELOW the (sticky) timeline date strip and above
              the table, filling the formerly empty band. Direct child of the
              tall wrapper, so it doesn't disturb the TimelineHeader's sticky
              containment (see comments above). */}
          {/* When active calls exist the bar pins itself sticky; in timeline
              mode it must tuck UNDER the sticky timeline date strip (48px
              filter bar + ~41px strip), otherwise directly under the filter
              bar. */}
          <ActivityBar stickyTopPx={isTimelineMode ? 89 : 48} />
          {/* Main Content with scrollable table - Header and content scroll together */}
          <div className="px-6 pl-[0px] pr-[0px]">
            <div className="relative">
              {/* FIXED: Single scroll container for both header and table content */}
              <div ref={setTableScrollContainer} className="sticky-table-container">
              <div className="transition-[min-width] duration-200" style={{ minWidth: activeMinWidth }}>
              {/* TimelineHeader was previously rendered here — see the
                  TimelineScaleProvider comment above for why it had to move
                  out of `.sticky-table-container`. */}
              {/* Sticky Table Header - scrolls horizontally with content.
                  Hidden + collapsed in timeline mode (Task #115); see the
                  `headerInLayout` / `headerVisible` state above. The
                  `transition-opacity` class is dropped under reduced-motion
                  so the swap is instant in both directions. */}
              {headerInLayout && (
              <div
                className={cn(
                  "sticky top-12 z-40 bg-gray-800",
                  !prefersReducedMotion && "transition-opacity duration-150 ease-out",
                )}
                style={{
                  opacity: headerVisible ? 1 : 0,
                  // Disable pointer events on the WHOLE wrapper while faded
                  // out (or fading out). Applying this to the inner header
                  // is not enough — the sticky wrapper itself still
                  // intercepts clicks in the overlap area otherwise, and
                  // can block the timeline overlay's top-right controls
                  // (Today, direction chevron, reset view) during the fade.
                  pointerEvents: headerVisible ? undefined : 'none',
                }}
                aria-hidden={headerVisible ? undefined : true}
                onTransitionEnd={handleHeaderTransitionEnd}
                data-testid="project-tree-column-header"
              >
                <div className="project-header rounded-t-lg border">
                  {/* FIXED: Use same CSS Grid as rows for perfect alignment */}
                  <div className="grid px-3 py-2 text-sm font-medium text-gray-300 border-b border-gray-600 gap-0 items-center" style={{ gridTemplateColumns: activeGridTemplate, ...activeColumnStyles }}>
                    {/* Thumbnail Column - Creation Date Sort */}
                    <div className="flex items-center justify-center border-r border-gray-600">
                      <button
                        onClick={() => filters.actions.handleColumnSort('createdAt')}
                        className={cn(
                          "p-0.5 rounded transition-colors",
                          filters.sorting.column === 'createdAt' 
                            ? "bg-blue-600 text-white" 
                            : "bg-gray-700 text-white hover:bg-gray-600"
                        )}
                        title="Sort by project creation date (chronological order)"
                        data-testid="sort-created-at"
                      >
                        {filters.sorting.column === 'createdAt' ? (
                          filters.sorting.direction === 'asc' ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowDownAZ className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    
                    {/* Expand Column */}
                    <div className="flex items-center justify-center border-r border-gray-600">
                      {/* Expand/Collapse header */}
                    </div>
                    
                    {/* ID Column */}
                    <button
                      onClick={() => filters.actions.handleColumnSort('id')}
                      className={cn(
                        "flex items-center border-r border-gray-600 px-2 hover:bg-gray-700 transition-colors",
                        filters.sorting.column === 'id' && "text-blue-400 font-semibold"
                      )}
                      data-testid="sort-id"
                    >
                      <span>ID</span>
                      {filters.sorting.column === 'id' && (
                        filters.sorting.direction === 'asc' ? (
                          <ChevronUp className="w-3 h-3 ml-1" />
                        ) : (
                          <ChevronDown className="w-3 h-3 ml-1" />
                        )
                      )}
                    </button>
                    
                    {/* Name/Project Column */}
                    <button
                      onClick={() => filters.actions.handleColumnSort('project')}
                      className={cn(
                        "px-3 flex items-center border-r border-gray-600 hover:bg-gray-700 transition-colors",
                        filters.sorting.column === 'project' && "text-blue-400 font-semibold"
                      )}
                      data-testid="sort-project"
                    >
                      <span>Project</span>
                      {filters.sorting.column === 'project' && (
                        filters.sorting.direction === 'asc' ? (
                          <ChevronUp className="w-3 h-3 ml-1" />
                        ) : (
                          <ChevronDown className="w-3 h-3 ml-1" />
                        )
                      )}
                    </button>
                  
                  {/* Lead Artist Column */}
                  <button
                    onClick={() => filters.actions.handleColumnSort('lead')}
                    className={cn(
                      "flex items-center px-2 hover:bg-gray-700 transition-colors",
                      filters.sorting.column === 'lead' && "text-blue-400 font-semibold"
                    )}
                    data-testid="sort-lead"
                  >
                    <span>Lead</span>
                    {filters.sorting.column === 'lead' && (
                      filters.sorting.direction === 'asc' ? (
                        <ChevronUp className="w-3 h-3 ml-1" />
                      ) : (
                        <ChevronDown className="w-3 h-3 ml-1" />
                      )
                    )}
                  </button>
                  
                  {/* Team Column */}
                  <div className="flex items-center pl-2">Artist</div>
                  
                  {/* Project Status Column */}
                  <button
                    onClick={() => filters.actions.handleColumnSort('status')}
                    className={cn(
                      "px-2 flex items-center hover:bg-gray-700 transition-colors",
                      filters.sorting.column === 'status' && "text-blue-400 font-semibold"
                    )}
                    data-testid="sort-status"
                  >
                    <span>Project Status</span>
                    {filters.sorting.column === 'status' && (
                      filters.sorting.direction === 'asc' ? (
                        <ChevronUp className="w-3 h-3 ml-1" />
                      ) : (
                        <ChevronDown className="w-3 h-3 ml-1" />
                      )
                    )}
                  </button>
                  
                  {/* Status Column */}
                  <div className="px-2">Status</div>
                  
                  {/* Timing Column */}
                  <div className="px-2 flex items-center">
                    <span>TIMING</span>
                    <Info className="w-4 h-4 ml-1 text-gray-500" />
                  </div>
                  
                  {/* Actions Column */}
                  <div className="flex items-center justify-end">Actions</div>
                  {showBudgetColumn && (
                    <div className="flex items-center px-2 text-amber-400">
                      <Shield className="w-3 h-3 mr-1" />
                      Budget
                    </div>
                  )}
                  </div>
                </div>
              </div>
              )}

              {/* Years and Projects Container */}
              <div
                ref={rowsContainerRef}
                className={`rounded-b-lg border-l border-r border-b relative ${isTimelineMode ? 'timeline-mode-active' : ''}`}
              >
              {/* Loading Skeleton - only wait for years data */}
              {data.yearsLoading ? (
                <div className="space-y-0">
                  {/* Skeleton for years and projects */}
                  {Array.from({ length: 3 }).map((_, yearIndex) => (
                    <div key={`skeleton-year-${yearIndex}`}>
                      {/* Year header skeleton */}
                      <div className="h-10 bg-gray-800 border-b border-gray-600 animate-pulse" />
                      {/* Project skeletons */}
                      {Array.from({ length: 4 }).map((_, projectIndex) => (
                        <div
                          key={`skeleton-project-${yearIndex}-${projectIndex}`}
                          className="h-12 bg-gray-900 border-b border-gray-600 animate-pulse"
                          style={{ minHeight: '48px' }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ) : (!filters.filteredYears || filters.filteredYears.length === 0) ? (
                <div className="text-center py-12 px-8 bg-gray-900/50">
                  <div className="text-gray-400 mb-4">
                    <Plus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <h3 className="text-lg font-medium text-gray-300 mb-2">No years found</h3>
                    <p className="text-sm">Click the "Add Year" button below to create your first year and start organizing projects.</p>
                  </div>
                </div>
              ) : (
                filters.filteredYears.map((year) => (
                  <div key={year.id}>
                    {renderYearHeader(year)}
                    
                    {expandedItems[`year-${year.id}`] && (
                      <>
                        {year.projects?.map((project) => (
                          <div key={project.id}>
                            <ProjectRow
                              project={{ ...project, pulseCount: displayedPulseCounts[project.id] ?? 0 }}
                              isExpanded={expandedItems[`project-${project.id}`] || false}
                              onToggleExpanded={() => toggleProjectManually(project.id)}
                              onOpenChat={handleOpenChat}
                              onDropFiles={(projectId, files) => actions.openAddTaskWithFiles(projectId, files)}
                              showBudgetColumn={showBudgetColumn}
                              budgetItems={bulkBudgetData?.[project.id]}
                              adminPulseVisibility={adminPulseVisibility}
                            />
                            {expandedItems[`project-${project.id}`] && (
                              <ProjectExpandedContent
                                project={project}
                                renderPulseTree={renderPulseTree}
                                selectedSequences={selectedSequences}
                                handleSequenceToggle={handleSequenceToggle}
                                expandedItems={expandedItems}
                                toggleExpanded={toggleExpanded}
                                renderTaskTree={renderTaskTree}
                                setAddingTaskForPulse={setAddingTaskForPulse}
                                addingTaskForPulse={addingTaskForPulse}
                                actions={actions}
                                handleOpenChat={handleOpenChat}
                                treeProjects={treeProjects}
                                createEffortPulseMutation={createEffortPulseMutation}
                                handleOpenTimePopup={handleOpenTimePopup}
                                timeTrackingSettings={timeTrackingSettings}
                                scrollTarget={scrollTarget}
                                onScrollComplete={handleScrollComplete}
                                manualExpandIntent={manualExpandIntents[project.id]}
                                onManualExpandComplete={completeManualExpansion}
                                setPulseSettingsTarget={setPulseSettingsTarget}
                                setPulseShareTarget={setPulseShareTarget}
                                showBudgetColumn={showBudgetColumn}
                              />
                            )}
                          </div>
                        ))}
                        
                        {/* Add Project row at year level */}
                        <div className="tree-item border-b border-gray-600">
                          <div className="flex px-3 items-center">
                            {/* Empty expand column */}
                            <div className="w-4 flex items-center justify-center flex-shrink-0 mr-1"></div>
                            
                            {/* Empty ID column */}
                            <div className="w-24 flex items-center flex-shrink-0"></div>

                            {/* Add Project button */}
                            <div className="flex-1 px-3">
                              <button
                                onClick={() => actions.openAddProjectDialog(year.fullYear.toString())}
                                className="text-sm text-gray-500 hover:text-blue-400 transition-colors flex items-center"
                                data-testid={`button-add-project-${year.id}`}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Project
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
              
              {/* Add Year button after all years */}
              <div className="tree-item border-b border-gray-600 navy-bg">
                <div className="flex px-3 items-center py-3 ml-[-52px]">
                  {/* Empty space for alignment */}
                  <div className="w-12 flex items-center justify-center flex-shrink-0 mr-1"></div>
                  <div className="w-4 flex items-center justify-center flex-shrink-0 mr-1"></div>
                  <div className="w-24 flex items-center flex-shrink-0"></div>
                  
                  {/* Add Year button */}
                  <div className="flex-1 px-3">
                    <button
                      onClick={() => actions.setShowAddYear(true)}
                      className="text-sm text-gray-400 hover:text-blue-400 transition-colors flex items-center"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Year
                    </button>
                  </div>
                </div>
              </div>

              {/* Lazy timeline overlay (sibling of the rows). Only mounted when
                  timeline mode is active so the chunk is loaded on demand. */}
              {isTimelineMode && (
                <Suspense fallback={null}>
                  <TimelineLayer
                    containerRef={rowsContainerRef}
                    projects={treeProjects}
                    selectedSequences={selectedSequences}
                    expandedItems={expandedItems}
                    active={isTimelineMode}
                    leftOffsetPx={TREE_STICKY_COL_PX}
                    resetSignal={timelineResetSignal}
                  />
                </Suspense>
              )}
              </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </TimelineScaleProvider>
      {/* Settings Modal */}
      <SettingsModal
        open={dialogs.settings}
        onOpenChange={actions.setShowSettings}
      />
      {/* Team Settings (task #52) — keychain lives here; visible to all users */}
      <TeamSettingsDialog
        open={showTeamSettings}
        onOpenChange={setShowTeamSettings}
      />
      {/* Add Year Dialog */}
      <AddYearDialog
        open={dialogs.addYear}
        onOpenChange={actions.setShowAddYear}
      />
      {/* Add Project Dialog */}
      <AddProjectDialog
        open={dialogs.addProject}
        onOpenChange={actions.setShowAddProject}
        defaultYearId={context.selectedYearId}
      />
      {/* Add Pulse Dialog */}
      <AddTaskDialog
        projectId={context.selectedProjectId}
        open={dialogs.addTask}
        onOpenChange={(open) => {
          actions.setShowAddTask(open);
          if (!open) {
            actions.setInitialDropFiles([]);
          }
        }}
        initialFiles={context.initialDropFiles}
      />
      {/* Add Sequence Dialog */}
      <AddSequenceDialog
        open={dialogs.addSequence}
        onOpenChange={actions.setShowAddSequence}
        projectId={context.selectedSequenceProjectId}
      />
      {/* Legacy ModernChat removed - V2 ChatDock is now the primary chat interface */}
      <MailOutDialog
        open={dialogs.mailOut}
        onOpenChange={actions.setShowMailOutDialog}
        projectId={context.selectedMailOutProjectId}
      />
      <MilestoneDialog
        open={dialogs.milestone}
        onOpenChange={actions.setShowMilestoneDialog}
        projectId={context.selectedMilestoneProjectId}
      />
      <TimeEntryPopup
        projectId={context.selectedTimeProjectId}
        pulseId={context.selectedTimePulseId}
        open={dialogs.timePopup}
        onOpenChange={actions.setShowTimePopup}
        mousePosition={context.timeMousePosition}
      />
      {/* Pulse Settings Dialog */}
      {pulseSettingsTarget && (
        <PulseSettingsDialog
          open={!!pulseSettingsTarget}
          onOpenChange={(open) => !open && setPulseSettingsTarget(null)}
          pulse={pulseSettingsTarget}
          isAdmin={user?.role === 'admin'}
        />
      )}
      {/* Pulse Share Dialog */}
      {pulseShareTarget && (
        <PulseShareDialog
          open={!!pulseShareTarget}
          onOpenChange={(open: boolean) => !open && setPulseShareTarget(null)}
          pulse={pulseShareTarget.pulse}
          projectId={pulseShareTarget.projectId}
          projectName={pulseShareTarget.projectName}
        />
      )}
    </div>
  );
}